import { Deporte, SubDeporte } from "@/types/category";
import { useEffect, useState } from "react";

/**
 * Builds the full sports list (with sub-sports) from the static locale
 * translations (`sportsTranslations` in `src/locales/<lang>/common.json`).
 *
 * The complete catalog of sports and categories already lives in the locale
 * files, so there is no need to hit the backend (`/api/deporte/{lang}`) nor to
 * persist anything in localStorage for this list.
 */
const buildSportsFromTranslations = (sportsTranslations: any): Deporte[] => {
    if (!sportsTranslations?.names) return [];

    const names: Record<string, string> = sportsTranslations.names;
    const subsports: Record<string, Record<string, string>> = sportsTranslations.subsports || {};

    // Object keys that look like integers are iterated in ascending numeric order,
    // so the list comes out ordered by sport id (same as before).
    return Object.entries(names).map(([idStr, nombre]) => {
        const idDeporte = parseInt(idStr);

        const subDeportes: SubDeporte[] = Object.entries(subsports[idStr] || {}).map(
            ([subIdStr, subNombre]) => ({
                idSubDeporte: parseInt(subIdStr),
                idDeporte,
                nombre: subNombre,
            })
        );

        return { idDeporte, nombre, subDeportes };
    });
};

type State = {
    data: (Deporte[])[];
    loading: boolean;
}

/**
 * Hook to get sports with their translations per language, built from the
 * static locale files (no API call, no localStorage cache).
 *
 * @param langs Array of languages (e.g., ['es', 'en', 'fr'])
 * @returns State with array of sports per language and loading status
 */
const useSports = (langs: string[]) => {
    const [state, setState] = useState<State>({
        loading: true,
        data: [...langs.map(() => [])]
    });

    useEffect(() => {
        setState({
            loading: true,
            data: [...langs.map(() => [])]
        })
        handleGetSports()
    }, [langs.join(',')])

    const handleGetSports = async () => {
        try {
            const sports: Deporte[][] = []

            for (const lang of langs) {
                // Import translations dynamically (one chunk per locale)
                const translations = await import(`@/locales/${lang}/common.json`)
                sports.push(buildSportsFromTranslations(translations.default?.sportsTranslations))
            }

            setState({
                loading: false,
                data: sports
            })
        } catch (e) {
            setState({
                loading: false,
                data: [...langs.map(() => [])]
            })
        }
    }

    return state
}

export default useSports
